import type * as React from 'react';
import { cn } from '../lib/utils.js';
import { APP_LOGO_NAMES, type AppLogoName } from './app-logo-names.js';

import AwsLogo from '../assets/app-logos/aws.svg?react';
import ChromeLogo from '../assets/app-logos/chrome.svg?react';
import ClaudeLogo from '../assets/app-logos/claude.svg?react';
import ClickhouseLogo from '../assets/app-logos/clickhouse.svg?react';
import CloudflareLogo from '../assets/app-logos/cloudflare.svg?react';
import CodexLogo from '../assets/app-logos/codex.svg?react';
import CursorLogo from '../assets/app-logos/cursor.svg?react';
import EdgeLogo from '../assets/app-logos/edge.svg?react';
import FacebookLogo from '../assets/app-logos/facebook.svg?react';
import GcpApiLogo from '../assets/app-logos/gcp-api.svg?react';
import GeminiLogo from '../assets/app-logos/gemini.svg?react';
import GithubLogo from '../assets/app-logos/github.svg?react';
import GmailLogo from '../assets/app-logos/gmail.svg?react';
import GoogleLogo from '../assets/app-logos/google.svg?react';
import HubspotLogo from '../assets/app-logos/hubspot.svg?react';
import InstagramLogo from '../assets/app-logos/instagram.svg?react';
import LinkedinLogo from '../assets/app-logos/linkedin.svg?react';
import LookerLogo from '../assets/app-logos/looker.svg?react';
import McpLogo from '../assets/app-logos/mcp.svg?react';
import MetaLogo from '../assets/app-logos/meta.svg?react';
import Microsoft365CopilotLogo from '../assets/app-logos/microsoft-365-copilot.svg?react';
import MicrosoftAzureLogo from '../assets/app-logos/microsoft-azure.svg?react';
import MicrosoftBingLogo from '../assets/app-logos/microsoft-bing.svg?react';
import MicrosoftExcelLogo from '../assets/app-logos/microsoft-excel.svg?react';
import MicrosoftOutlookLogo from '../assets/app-logos/microsoft-outlook.svg?react';
import MicrosoftTeamsLogo from '../assets/app-logos/microsoft-teams.svg?react';
import MicrosoftWordLogo from '../assets/app-logos/microsoft-word.svg?react';
import MicrosoftLogo from '../assets/app-logos/microsoft.svg?react';
import NotionLogo from '../assets/app-logos/notion.svg?react';
import OpenaiLogo from '../assets/app-logos/openai.svg?react';
import PerplexityLogo from '../assets/app-logos/perplexity.svg?react';
import RedditLogo from '../assets/app-logos/reddit.svg?react';
import SalesforceLogo from '../assets/app-logos/salesforce.svg?react';
import SlackLogo from '../assets/app-logos/slack.svg?react';
import SnapchatLogo from '../assets/app-logos/snapchat.svg?react';
import StripeLogo from '../assets/app-logos/stripe.svg?react';
import SupabaseLogo from '../assets/app-logos/supabase.svg?react';
import TiktokLogo from '../assets/app-logos/tiktok.svg?react';
import XLogo from '../assets/app-logos/x.svg?react';
import YoutubeLogo from '../assets/app-logos/youtube.svg?react';

// Re-export so consumers can reach the name list/type via the same subpath
// (@parcel/ui/app-logo) — the ./components/* export wildcard only maps .tsx.
export { APP_LOGO_NAMES, type AppLogoName };

type SvgComponent = React.FC<React.SVGProps<SVGSVGElement>>;

const LOGOS: Record<AppLogoName, SvgComponent> = {
  aws: AwsLogo,
  chrome: ChromeLogo,
  claude: ClaudeLogo,
  clickhouse: ClickhouseLogo,
  cloudflare: CloudflareLogo,
  codex: CodexLogo,
  cursor: CursorLogo,
  edge: EdgeLogo,
  facebook: FacebookLogo,
  'gcp-api': GcpApiLogo,
  gemini: GeminiLogo,
  github: GithubLogo,
  gmail: GmailLogo,
  google: GoogleLogo,
  hubspot: HubspotLogo,
  instagram: InstagramLogo,
  linkedin: LinkedinLogo,
  looker: LookerLogo,
  mcp: McpLogo,
  meta: MetaLogo,
  microsoft: MicrosoftLogo,
  'microsoft-365-copilot': Microsoft365CopilotLogo,
  'microsoft-azure': MicrosoftAzureLogo,
  'microsoft-bing': MicrosoftBingLogo,
  'microsoft-excel': MicrosoftExcelLogo,
  'microsoft-outlook': MicrosoftOutlookLogo,
  'microsoft-teams': MicrosoftTeamsLogo,
  'microsoft-word': MicrosoftWordLogo,
  notion: NotionLogo,
  openai: OpenaiLogo,
  perplexity: PerplexityLogo,
  reddit: RedditLogo,
  salesforce: SalesforceLogo,
  slack: SlackLogo,
  snapchat: SnapchatLogo,
  stripe: StripeLogo,
  supabase: SupabaseLogo,
  tiktok: TiktokLogo,
  x: XLogo,
  youtube: YoutubeLogo,
};

const SIZES = { sm: 16, md: 24, lg: 40 } as const;
export type AppLogoSize = keyof typeof SIZES;

export interface AppLogoProps extends Omit<React.SVGProps<SVGSVGElement>, 'name'> {
  /** Which app logo to render. Compile-checked against the known set. */
  name: AppLogoName;
  /** Preset height (sm=16, md=24, lg=40) or an explicit pixel height. */
  size?: AppLogoSize | number;
  /** Accessible label; defaults to the logo name. */
  title?: string;
}

/**
 * Renders a third-party app logo as an inline SVG. Sizes by height with
 * automatic width so non-square logos keep their aspect ratio. Monochrome
 * marks (github, x, openai, codex, cursor, mcp) use currentColor — pass a text color via
 * className (e.g. "text-foreground") to make them adapt to dark mode.
 *
 * Separate from the logo.dev dynamic company-logo system — do not mix.
 */
export function AppLogo({ name, size = 'md', className, title, style, ...props }: AppLogoProps) {
  const Svg = LOGOS[name];
  const px = typeof size === 'number' ? size : SIZES[size];

  if (!Svg) {
    // Defensive: `name` is compile-checked, but guard runtime-sourced values.
    return (
      <span
        role="img"
        aria-label={title ?? String(name)}
        className={cn('inline-block shrink-0 rounded-sm bg-muted align-middle', className)}
        style={{ width: px, height: px, ...style }}
      />
    );
  }

  return (
    <Svg
      role="img"
      aria-label={title ?? name}
      className={cn('inline-block shrink-0 align-middle', className)}
      style={{ height: px, width: 'auto', ...style }}
      {...props}
    />
  );
}
